import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Chip, Stack } from '@mui/material';

import CustomButton from 'components/button/CustomButton';
import MasterTypeChip from 'components/chip/MasterTypeChip';
import Circular from 'components/common/CustomCircular';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleHeaderAction,
  ConsoleSearchFilter,
  ConsoleTable,
  useQueryInitialFilters,
} from 'components/console';
import {
  CsvBulkUploadDialog,
  CsvTemplateDownloadDialog,
  downloadCsvTemplate,
} from 'components/csv';
import { CsvBulkConfig } from 'components/csv/types';
import { ClientResult, CreateClientParams, listClient } from 'stores/ClientStoreAdapter';
import { MasterTypeResult, getClientType } from 'stores/MasterTypeStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault } from 'utils/Toastify';

import ClientCreateForm, { ClientCreateFormHandle } from './components/ClientCreateForm';
import ClientUpdateForm, { ClientUpdateFormHandle } from './components/ClientUpdateForm';
import { createClientBulkConfig, createClientCsvFields } from './components/clientCsvConfig';

type DrawerMode = 'none' | 'create' | 'detail';

const ClientManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [isProgress, setProgress] = useState(true);
  const [clients, setClients] = useState<ClientResult[]>([]);
  const [clientTypes, setClientTypes] = useState<MasterTypeResult[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientResult | null>(null);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('none');
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [isCsvTemplateDialogOpen, setIsCsvTemplateDialogOpen] = useState(false);
  const [isCsvBulkDialogOpen, setIsCsvBulkDialogOpen] = useState(false);
  // 現在適用中の検索パラメータ（作成/更新/CSV後・再読み込みで同条件を再検索するため保持）
  const [activeParams, setActiveParams] = useState<Record<string, string>>({});

  const createFormRef = useRef<ClientCreateFormHandle>(null);
  const updateFormRef = useRef<ClientUpdateFormHandle>(null);

  useEffect(() => {
    getClientType()
      .then(setClientTypes)
      .catch(() => {});
  }, []);

  // 現在適用中の検索条件で再検索する
  const refetch = () => fetchClients(activeParams);

  const fetchClients = (params: Record<string, string>) => {
    setProgress(true);
    listClient(params)
      .then((result: ClientResult[]) => {
        setClients(result);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('取引先情報の取得に失敗しました');
      });
  };

  const closeDrawer = () => {
    setDrawerMode('none');
    setSelectedClient(null);
    setIsEditingClient(false);
  };

  // 作成・更新後は共通で「フォームを閉じて現在の検索条件で再検索」
  const handleCreated = () => {
    closeDrawer();
    refetch();
  };

  const handleClientUpdated = () => {
    closeDrawer();
    refetch();
  };

  const handleSearch = (filters: Filter[]) => {
    closeDrawer();
    const params: Record<string, string> = {};
    filters.forEach((f) => {
      params[f.key] = f.value.key;
    });
    setActiveParams(params);
    fetchClients(params);
  };

  const handleCreateClick = () => {
    setSelectedClient(null);
    setIsEditingClient(false);
    setDrawerMode('create');
  };

  const handleRowClick = (row: ClientResult) => {
    setIsEditingClient(false);
    setSelectedClient(row);
    setDrawerMode('detail');
  };

  const typeIndexOf = (clientTypeId: string) =>
    clientTypes.findIndex((t) => t.typeCode === clientTypeId);

  const searchConditions: Condition[] = useMemo(
    () => [
      { key: 'clientName', name: '取引先名', type: 'text', color: '#78C1F3' },
      { key: 'clientNameKana', name: '取引先名（カナ）', type: 'katakana-text', color: '#3A98B9' },
      {
        key: 'clientTypeId',
        name: '取引先区分',
        type: 'select',
        color: '#B799FF',
        values: clientTypes.map((t) => ({ key: t.typeCode, name: t.typeLabel })),
      },
      {
        key: 'includeExpired',
        name: '取引終了を含む',
        type: 'check',
        color: '#78909C',
        value: { key: 'true', name: '取引終了を含む' },
      },
    ],
    [clientTypes],
  );

  // URL クエリパラメータを初期検索条件として反映
  const { queryParams, initialFilters } = useQueryInitialFilters(searchConditions);
  useEffect(() => {
    setActiveParams(queryParams);
    fetchClients(queryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const csvFields = useMemo(() => createClientCsvFields(clientTypes), [clientTypes]);
  const csvBulkConfig: CsvBulkConfig<CreateClientParams> | null = useMemo(
    () => (clientTypes.length > 0 ? createClientBulkConfig(clientTypes) : null),
    [clientTypes],
  );
  const handleCsvTemplateDownload = () => {
    downloadCsvTemplate(csvFields, '取引先一括登録テンプレート.csv');
  };

  const menuActions: ConsoleHeaderAction[] = [
    {
      label: 'CSV一括登録',
      icon: <UploadFileIcon sx={{ fontSize: 18 }} />,
      onClick: () => setIsCsvBulkDialogOpen(true),
    },
    {
      label: 'CSVテンプレートをダウンロード',
      icon: <FileDownloadIcon sx={{ fontSize: 18 }} />,
      onClick: () => setIsCsvTemplateDialogOpen(true),
    },
  ];

  const columns: ConsoleColumn<ClientResult>[] = [
    {
      key: 'clientName',
      header: '取引先名',
      width: 240,
      ellipsis: (c) => c.clientName,
    },
    {
      key: 'kana',
      header: 'カナ',
      width: 180,
      ellipsis: (c) => c.clientNameKana,
    },
    {
      key: 'type',
      header: '区分',
      width: 100,
      render: (c) => {
        const idx = typeIndexOf(c.clientTypeId);
        return idx >= 0 ? (
          <MasterTypeChip label={clientTypes[idx].typeLabel} colorIndex={idx} size="small" />
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>
            —
          </Box>
        );
      },
    },
    {
      key: 'contactName',
      header: '担当者',
      width: 140,
      ellipsis: (c) => c.contactName ?? '',
    },
    {
      key: 'status',
      header: '状態',
      width: 90,
      render: (c) => {
        const active = !c.contractEndDate || new Date(c.contractEndDate) >= new Date();
        return (
          <Chip
            label={active ? '取引中' : '取引終了'}
            size="small"
            color={active ? 'primary' : 'default'}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        );
      },
    },
    {
      key: 'period',
      header: '取引期間',
      width: 170,
      render: (c) => (
        <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {c.contractStartDate ? formatDate(c.contractStartDate) : '—'}
          {c.contractEndDate ? ` 〜 ${formatDate(c.contractEndDate)}` : ''}
        </Box>
      ),
    },
  ];

  const selectedActive =
    selectedClient &&
    (!selectedClient.contractEndDate || new Date(selectedClient.contractEndDate) >= new Date());

  const drawerFooter =
    drawerMode === 'create' ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton label="キャンセル" buttonVariant="secondary" onClick={closeDrawer} />
        <CustomButton
          label="作成"
          buttonVariant="primary"
          icon={<AddBusinessIcon />}
          onClick={() => createFormRef.current?.submit()}
        />
      </Stack>
    ) : selectedClient && isEditingClient ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="キャンセル"
          buttonVariant="secondary"
          onClick={() => setIsEditingClient(false)}
        />
        <CustomButton
          label="更新"
          buttonVariant="primary"
          icon={<SaveIcon />}
          onClick={() => updateFormRef.current?.submit()}
        />
      </Stack>
    ) : selectedClient ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="編集"
          buttonVariant="primary"
          icon={<EditIcon />}
          onClick={() => setIsEditingClient(true)}
        />
      </Stack>
    ) : undefined;

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <ConsoleHeader
        title="取引先"
        count={clients.length}
        description="取引先企業の登録・更新・検索を行います"
        backLabel="要員管理"
        onBack={() => navigate('/member')}
        onRefresh={refetch}
        menuActions={menuActions}
        actions={
          <CustomButton
            label="取引先を登録"
            buttonVariant="primary"
            icon={<AddBusinessIcon />}
            onClick={handleCreateClick}
          />
        }
      />

      <ConsoleTable
        columns={columns}
        rows={clients}
        rowKey={(c) => c.clientId}
        selectedKey={drawerMode === 'detail' ? selectedClient?.clientId : undefined}
        onRowClick={handleRowClick}
        emptyMessage="取引先がありません。「取引先を登録」または「CSV一括登録」から登録してください。"
        toolbar={
          <ConsoleSearchFilter
            conditions={searchConditions}
            initialFilters={initialFilters}
            onFiltersChange={handleSearch}
            conditionKey="client-list"
            userProfileKey="member-client-list-search"
          />
        }
      />

      <ConsoleDetailDrawer
        open={drawerMode === 'create'}
        onClose={closeDrawer}
        title="取引先の登録"
        width={640}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ClientCreateForm
              ref={createFormRef}
              clientTypes={clientTypes}
              onCreate={handleCreated}
              onCancel={closeDrawer}
              setProgress={setProgress}
              hideHeader
            />
          </Box>
          <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
        </Box>
      </ConsoleDetailDrawer>

      <ConsoleDetailDrawer
        open={drawerMode === 'detail' && selectedClient !== null}
        onClose={closeDrawer}
        title={selectedClient?.clientName ?? ''}
        badges={
          selectedClient && (
            <Chip
              label={selectedActive ? '取引中' : '取引終了'}
              size="small"
              color={selectedActive ? 'primary' : 'default'}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )
        }
        width={640}
      >
        {selectedClient && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ClientUpdateForm
                ref={updateFormRef}
                client={selectedClient}
                clientTypes={clientTypes}
                isEditing={isEditingClient}
                onClientUpdated={handleClientUpdated}
                onClose={closeDrawer}
                onEditingChange={setIsEditingClient}
                setProgress={setProgress}
                hideHeader
              />
            </Box>
            {drawerFooter && (
              <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
            )}
          </Box>
        )}
      </ConsoleDetailDrawer>

      <CsvTemplateDownloadDialog
        open={isCsvTemplateDialogOpen}
        onClose={() => setIsCsvTemplateDialogOpen(false)}
        entityName="取引先"
        fields={csvFields}
        onDownload={handleCsvTemplateDownload}
      />

      {csvBulkConfig && (
        <CsvBulkUploadDialog
          open={isCsvBulkDialogOpen}
          onClose={() => setIsCsvBulkDialogOpen(false)}
          config={csvBulkConfig}
          onComplete={() => {
            closeDrawer();
            refetch();
          }}
        />
      )}

      <Circular open={isProgress} />
    </Box>
  );
};

export default ClientManagementPage;
